/**
 * husl-stylist-attribution.js
 *
 * HUSL Beauty Stylist Attribution Engine
 *
 * Attribution priority (last-touch):
 *   1. Explicit link  — URL has ?stylist=<slug>. Always overwrites stored attribution.
 *   2. Stylist page   — Visiting a stylist page always overwrites attribution to that
 *                       stylist (last stylist page visited wins commission).
 *   3. Restore        — On every other page (e.g. shampoo / collections), push the
 *                       stored attribution into cart attributes so checkout still
 *                       commissions the last stylist. Never clears cart line items.
 *
 * Storage:
 *   Cookie (365-day): husl_stl_ref          — unique_code or SEO slug
 *   localStorage:     husl_stl_ref          — same (backup, survives cookie clear)
 *                     husl_stl_name         — stylist display name (for banner)
 *                     husl_stl_page         — stylist page handle (for banner link)
 *                     husl_stl_dismissed    — banner dismiss flag (session)
 *
 * Cart attributes only (POST /cart/update.js) — does not modify line items:
 *   _stylist_ref    — unique_code when known, else SEO slug (read by BE order webhook)
 *   _stylist_name   — display name (informational)
 *   _stylist_set_at — ISO timestamp of when attribution was captured
 */

(function () {
  'use strict';

  var COOKIE_KEY  = 'husl_stl_ref';
  var LS_SLUG     = 'husl_stl_ref';
  var LS_NAME     = 'husl_stl_name';
  var LS_PAGE     = 'husl_stl_page';
  var LS_DISMISSED = 'husl_stl_dismissed';
  var COOKIE_DAYS = 365;

  /* ------------------------------------------------------------------ */
  /* Cookie helpers                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * Set a first-party cookie.
   * @param {string} name
   * @param {string} value
   * @param {number} days
   */
  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = (
      name + '=' + encodeURIComponent(value) +
      '; expires=' + d.toUTCString() +
      '; path=/; SameSite=Lax' + secure
    );
  }

  /**
   * Read a cookie by name.
   * @param {string} name
   * @returns {string|null}
   */
  function getCookie(name) {
    var nameEq = name + '=';
    var parts  = document.cookie.split(';');
    for (var i = 0; i < parts.length; i++) {
      var c = parts[i].replace(/^\s+/, '');
      if (c.indexOf(nameEq) === 0) {
        return decodeURIComponent(c.substring(nameEq.length));
      }
    }
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* localStorage helpers (silent-fail in private browsing)              */
  /* ------------------------------------------------------------------ */

  /** @param {string} key @param {string} value */
  function lsSet(key, value) { try { localStorage.setItem(key, value); } catch (e) {} }

  /** @param {string} key @returns {string|null} */
  function lsGet(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }

  /** @param {string} key */
  function lsClear(key) { try { localStorage.removeItem(key); } catch (e) {} }

  /* ------------------------------------------------------------------ */
  /* Shopify cart attributes                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Write attribution cart attributes without touching line items or other attrs.
   * @param {string} slug
   * @param {string} name
   */
  function setCartAttributes(slug, name) {
    var attrs = {};
    attrs['_stylist_ref']    = slug;
    attrs['_stylist_name']   = name || slug;
    attrs['_stylist_set_at'] = new Date().toISOString();
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {});
  }

  /* ------------------------------------------------------------------ */
  /* URL param helper                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * Read ?stylist= from the current URL.
   * @returns {string|null}
   */
  function getSlugFromUrl() {
    var params = new URLSearchParams(location.search);
    return params.get('stylist') || null;
  }

  /* ------------------------------------------------------------------ */
  /* Persist attribution                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Store attribution data and push to cart attributes only.
   * @param {string} slug
   * @param {string} name
   * @param {string} pageHandle
   */
  function persistAttribution(slug, name, pageHandle) {
    var previous = getCookie(COOKIE_KEY) || lsGet(LS_SLUG);
    if (previous && previous !== slug) {
      lsClear(LS_DISMISSED);
    }
    setCookie(COOKIE_KEY, slug, COOKIE_DAYS);
    lsSet(LS_SLUG, slug);
    lsSet(LS_NAME, name || slug);
    if (pageHandle) lsSet(LS_PAGE, pageHandle);
    setCartAttributes(slug, name);
  }

  /**
   * Attribution token for cart/backend: prefer unique_code over SEO slug.
   * @param {object|null} stylist
   * @returns {string|null}
   */
  function getAttributionCode(stylist) {
    if (stylist && stylist.uniqueCode) return stylist.uniqueCode;
    if (stylist && stylist.slug) return stylist.slug;
    return null;
  }

  /* ------------------------------------------------------------------ */
  /* Initialisation                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * Entry point. Runs after the DOM is ready.
   *
   * Decision tree:
   *   A. URL has ?stylist=<slug>  → overwrite to that stylist (prefer uniqueCode on page).
   *   B. Stylist page             → last-touch: always overwrite to this page's stylist.
   *   C. Any other page           → restore stored attribution into cart attributes only.
   */
  function init() {
    var urlSlug  = getSlugFromUrl();
    var stylist  = window.__huslStylist || null;

    /* Case A: explicit link — always overwrite */
    if (urlSlug) {
      /* Shared-cart links also carry stylist_name / stylist_page so the banner and
         _stylist_name are right for someone who has never visited the stylist's page. */
      var params      = new URLSearchParams(location.search);
      var urlName     = params.get('stylist_name') || '';
      var urlPage     = params.get('stylist_page') || '';
      var previousRef = getCookie(COOKIE_KEY) || lsGet(LS_SLUG);
      var storedName  = (previousRef === urlSlug) ? lsGet(LS_NAME) : null;
      var name        = (stylist && stylist.slug === urlSlug) ? (stylist.displayName || urlSlug) : (urlName || storedName || urlSlug);
      var pageHandle  = (stylist && stylist.slug === urlSlug) ? (stylist.pageHandle || '') : urlPage;
      var code        = (stylist && stylist.slug === urlSlug && stylist.uniqueCode)
        ? stylist.uniqueCode
        : urlSlug;
      persistAttribution(code, name, pageHandle);
      return;
    }

    /* Case B: stylist page — last touch wins */
    if (stylist && (stylist.uniqueCode || stylist.slug)) {
      var attributionCode = getAttributionCode(stylist);
      persistAttribution(attributionCode, stylist.displayName, stylist.pageHandle);
      return;
    }

    /* Case C: any other page — restore attribution attrs only (keep line items) */
    var storedSlug = getCookie(COOKIE_KEY) || lsGet(LS_SLUG);
    if (storedSlug) {
      var storedName = lsGet(LS_NAME) || storedSlug;
      setCartAttributes(storedSlug, storedName);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Public accessor                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Read-only access to stored attribution for other theme scripts
   * (e.g. the PDP Buy It Now permalink, which bypasses the cart).
   */
  window.HUSLAttribution = {
    getSlug: function () { return getCookie(COOKIE_KEY) || lsGet(LS_SLUG) || null; },
    getName: function () { return lsGet(LS_NAME) || null; }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
